import type FormPage from '@/modules/common/common/models/APP/FormPage'
import type Insured from '@/modules/common/common/models/APP/Insured'
import { UserRoleEnum } from '../models/enums'

export function usePagesBuilder() {
  function getPageInsureds<I extends Insured = Insured>(holder: I, insureds: I[]) {
    if (holder.role === UserRoleEnum.HOLDER_NOT_INSURED) {
      return [holder, ...insureds]
    }

    return insureds
  }
  function getFirstPageName(pages: Record<string, FormPage>) {
    return Object.keys(pages)[0]
  }
  function getLastPageName(pages: Record<string, FormPage>) {
    const length = Object.keys(pages).length - 1
    return Object.keys(pages)[length]
  }

  function getDynamicPages<I extends Insured = Insured>(
    pages: Record<string, FormPage>,
    insureds: I[]
  ): Record<string, FormPage> {
    let _pages = {}

    insureds.forEach((insured, roleIndex) => {
      Object.entries(pages).forEach(([pageKey, pageValues]) => {
        const page = pageValues as FormPage

        const guardValid = page.extra?.guards?.every((f: Function) => f(insured)) ?? true
        const validRole = page.extra?.roles?.includes(insured.role) ?? true

        if (validRole && guardValid) {
          const nextPages = getNextPageEnable<I>(pages, pageKey, insureds, roleIndex)

          const newPage: any = {
            form: page.form,
            next: nextPages,
            extra: {
              ...page.extra,
              role: insured.role,
              insured: roleIndex
            }
          }

          _pages = { ..._pages, [renamePageKey(pageKey, roleIndex)]: newPage }
        }
      })
    })

    return _pages
  }

  function getNextPageEnable<I extends Insured = Insured>(
    pages: Record<string, FormPage>,
    currentPageName: string,
    insureds: I[],
    roleIndex: number
  ) {
    const currentPage = pages[currentPageName]
    if (!currentPage) {
      return null
    }

    const role = insureds[roleIndex].role
    const pageIndex = Object.keys(pages).findIndex((p) => p === currentPageName)

    const next = pages[currentPage.next[0]]
    const guardValid = next?.extra?.guards?.every((f: Function) => f(insureds[roleIndex])) ?? true
    const validRole = next?.extra?.roles?.includes(role) ?? true
    if (validRole && guardValid) {
      if (pageIndex < Object.keys(pages).length - 1) {
        return currentPage.next.map((n) => renamePageKey(n, roleIndex))
      }

      if (roleIndex < insureds.length - 1) {
        const firstPage = Object.keys(pages)[0]
        return [renamePageKey(firstPage, roleIndex + 1)]
      }

      return currentPage.next
    }

    const nextRoleIndex = pageIndex < Object.keys(pages).length - 1 ? roleIndex : ++roleIndex
    return getNextPageEnable(pages, currentPage.next[0], insureds, nextRoleIndex)
  }

  function renamePageKey(pageKey: string, index: number) {
    return pageKey.replace(/(\d_)(\d)/g, `$1${index}_$2`)
  }

  return {
    getPageInsureds,
    getFirstPageName,
    getLastPageName,
    getDynamicPages
  }
}
